import axios from 'axios';

// Get the backend API URL from the environment variables set in Vercel.
const apiUrl = process.env.REACT_APP_API_URL;

// This console.log is very helpful for debugging.
// It will print the URL to your browser's console when the app loads.
// console.log("VERCEL BUILD - API Base URL:", apiUrl); 

// Create axios instance with base configuration
const api = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout (increased for complex analysis)
  // Note: Individual requests can override this timeout
});

// --- DEBUG LOG: API Client Configuration ---
console.log("--- DEBUG: Frontend API Client Initialized ---");
console.log("Base URL configured:", api.defaults.baseURL);
console.log("Full API URL:", apiUrl);

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
  (config) => {
    // --- DEBUG LOG: Request Details ---
    const fullUrl = `${config.baseURL}${config.url}`;
    console.log('--- DEBUG: API Request Interceptor ---');
    console.log('Method:', config.method?.toUpperCase());
    console.log('URL path:', config.url);
    console.log('Base URL:', config.baseURL);
    console.log('Full URL:', fullUrl);
    console.log('Headers:', config.headers);
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Auth token added');
    } else {
      console.log('No auth token found');
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors and token refresh
api.interceptors.response.use(
  (response) => {
    // console.log('API Response:', response.status, response.config.url, response.data);
    return response;
  },
  async (error) => {
    console.error('API Response Error:', error.response?.status, error.response?.data, error.message);
    
    const originalRequest = error.config;
    
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      const errorDetail = error.response?.data?.detail || '';
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath === '/login' || 
                         currentPath === '/signup' || 
                         currentPath === '/forgot-password' ||
                         currentPath === '/reset-password';
      
      // Check if this is a Zerodha session expiry error
      if (errorDetail.toLowerCase().includes('zerodha session expired')) {
        // Trigger broker reconnect modal
        // Dispatch a custom event that will be caught by AuthContext
        window.dispatchEvent(new CustomEvent('broker-reconnect-required', {
          detail: {
            broker: 'ZERODHA',
            error: errorDetail,
            failedRequest: {
              method: error.config.method,
              url: error.config.url,
              data: error.config.data,
              params: error.config.params,
            }
          }
        }));
        
        // Don't redirect, just reject the promise
        return Promise.reject(error);
      }
      
      // Try to refresh token if we have a refresh token and this isn't a retry
      if (!originalRequest._retry && !isAuthPage) {
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (refreshToken) {
          originalRequest._retry = true; // Mark as retry to prevent infinite loops
          
          try {
            // Attempt to refresh the access token
            const refreshResponse = await axios.post(
              `${apiUrl}/api/v1/refresh_token`,
              { refresh_token: refreshToken },
              {
                headers: {
                  'Content-Type': 'application/json',
                },
              }
            );
            
            const { access_token, refresh_token: newRefreshToken } = refreshResponse.data;
            
            // Update tokens in localStorage
            localStorage.setItem('token', access_token);
            if (newRefreshToken) {
              localStorage.setItem('refreshToken', newRefreshToken);
            }
            
            // Update the authorization header for the original request
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            
            // Retry the original request with the new token
            return api(originalRequest);
          } catch (refreshError) {
            // If refresh token is also invalid, logout the user
            console.error('Refresh token failed:', refreshError);
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            
            // Only redirect if we're not already on an auth page
            if (!isAuthPage) {
              window.location.href = '/login';
            }
            
            return Promise.reject(refreshError);
          }
        } else {
          // No refresh token available, standard 401 handling
          if (!isAuthPage) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
          }
        }
      } else if (!isAuthPage && !originalRequest._retry) {
        // Standard 401 handling when refresh token is not available
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
